import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Login.css';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    password: '',
    password_confirmation: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get code and email from URL parameters (if coming from forgot password)
    const code = searchParams.get('code');
    const email = searchParams.get('email');

    if (code && email) {
      setFormData(prev => ({
        ...prev,
        code: code,
        email: decodeURIComponent(email),
      }));
    } else if (!email) {
      // If no email, show error
      setError('Please request a password reset first.');
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate code
    if (!formData.code || formData.code.length !== 6) {
      setError('Please enter a valid 6-digit reset code.');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.resetPassword({
        email: formData.email,
        code: formData.code,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to reset password. The code may be invalid or expired. Please request a new password reset.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Header />
        <div className="container">
          <div className="login-page">
            <h1>Password Reset Successful</h1>
            <div className="alert alert-success" style={{ 
              padding: '1rem', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              borderRadius: '4px',
              color: '#155724',
              marginBottom: '1rem'
            }}>
              Your password has been reset successfully. Redirecting to login...
            </div>
            <Link to="/login" className="btn btn-primary">Go to Login</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="login-page">
          <h1>Reset Password</h1>
          
          {error && <div className="error-message">{error}</div>}

          {formData.email ? (
            <form onSubmit={handleSubmit}>
              {!formData.code && (
                <div className="form-group">
                  <label htmlFor="code">Reset Code</label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setFormData({
                        ...formData,
                        code: value,
                      });
                      setError('');
                    }}
                    required
                    maxLength={6}
                    disabled={loading}
                    placeholder="000000"
                    style={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      letterSpacing: '0.5rem',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      width: '100%'
                    }}
                    autoComplete="off"
                    autoFocus
                  />
                  <small>Enter the 6-digit code from your email</small>
                </div>
              )}

              {formData.code && (
                <>
                  <div className="alert alert-info" style={{ 
                    padding: '0.75rem', 
                    backgroundColor: '#d1ecf1', 
                    border: '1px solid #bee5eb', 
                    borderRadius: '4px',
                    color: '#0c5460',
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                  }}>
                    Reset code: <strong>{formData.code}</strong> for <strong>{formData.email}</strong>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">New Password</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                      disabled={loading}
                      minLength={8}
                      placeholder="Enter new password (min 8 characters)"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Password must be at least 8 characters long.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password_confirmation">Confirm New Password</label>
                    <input
                      type="password"
                      id="password_confirmation"
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                      disabled={loading}
                      minLength={8}
                      placeholder="Confirm new password"
                    />
                  </div>
                </>
              )}

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || (formData.code && formData.code.length !== 6)}
                style={{ width: '100%', marginBottom: formData.code ? '0.5rem' : '0' }}
              >
                {loading ? 'Resetting Password...' : formData.code ? 'Reset Password' : 'Continue'}
              </button>

              {formData.code && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      code: '',
                    });
                    setError('');
                  }}
                  style={{ width: '100%' }}
                >
                  Change Code
                </button>
              )}
            </form>
          ) : (
            <div className="alert alert-danger" style={{ 
              padding: '1rem', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb', 
              borderRadius: '4px',
              color: '#721c24'
            }}>
              {error || 'Please request a password reset first.'}
              <div style={{ marginTop: '1rem' }}>
                <Link to="/forgot-password" className="btn btn-primary">
                  Request Password Reset
                </Link>
              </div>
            </div>
          )}

          <p className="register-link" style={{ marginTop: '1rem' }}>
            <Link to="/login">Back to Login</Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default ResetPassword;
